/**
 * OperationsOverview.tsx — Operations / Overview (M6, the department KPI
 * surface).
 *
 * The mockup's Overview (klaxiora-crm-mockup-full.html :1678-1718): two KPI
 * rows under an "Operational health" divider, a Recent Activity feed, and
 * an Open Incidents panel. Everything here is computed CLIENT-SIDE over the
 * five shipped list endpoints (overviewView.ts owns the formulas and the
 * stated §B.1 deviation + limit-200 ceiling note). Pure field-ops — no
 * run/agent-platform KPIs, and the subtitle drops the mockup's "agents,
 * runs and schedules" leakage (:1682, flagged in design doc §A).
 *
 * Honesty calls (decisions, M6) visible in the cards: "Team with tasks"
 * replaces the mockup's "Active agents" (no field-agent registry exists);
 * completion rate is month-scoped so its line is true; audit pass rate
 * carries the Quality page's own all-time label; tasks-today runs on the
 * real clock.
 *
 * Mockup-parity on interactivity: cards, feed items and incident rows are
 * NON-CLICKABLE — the mockup attaches no listeners to any of them
 * (:1694-1705, :3868-3875, :5125-5129). Linking items through to their
 * module pages is later polish, not M6.
 *
 * Each list loads independently: a failed list degrades its own section
 * (error line + '—' values) and never blanks the page.
 *
 * S-Tier (slice 2): this page renders the k-* component contract from
 * src/styles/klaxiora.css directly rather than through the shared Closr
 * primitives, so Operations can adopt the system without moving eight other
 * departments' baselines. Cards were already S-Tier before this slice — the
 * foundation's token rekey made .closr-card resolve to .k-card's exact
 * declarations — so what changes here is the metric widget (which gains the
 * 160ms hover it never had), the card head, the section eyebrow and badges.
 * Colours still flow from src/theme/tokens.ts; no raw values.
 */
import { SectionTitle, LoadingState, EmptyState } from '../../DashboardShared'
import { useAsync, fetchTasks, fetchIncidents, fetchAssets, fetchSops, fetchQualityChecks } from '../../api'
import { danger, success, surface, ink } from '../../../../theme/tokens'
import { incidentKpis, severityLabel, severityIsSevere } from './incidentsView'
import { assetKpis } from './assetsView'
import { qcKpis } from './qualityView'
import { taskKpis, openIncidentsSubline, activityFeed, openIncidentsPanel } from './overviewView'
import { formatSopDate } from './sopsView'

function SectionErrorNote({ message }: { message: string }) {
  return <div className="text-12 text-danger">{message}</div>
}

/**
 * The k-metric widget, written once and exported to the other Operations
 * pages. NOT a new shared primitive: it lives with the Operations pages and is
 * markup-level reuse of the k-* contract, so DashboardShared's MetricCard (and
 * its eight non-Operations consumers) stays untouched. Duplicating this block
 * across the five metric-bearing Operations pages would be 25 copies.
 *
 * NOTE: .k-metric-value and .k-card-title now carry explicit ink in the
 * adopted sheet (the foundation gap-fix, ADOPTION.md §7) — the bundle set no
 * colour on either and assumed a shell wrapper this app doesn't have, which
 * washed both out on the light ground until slice 2 patched each call-site.
 * The text-ink-primary utilities here are that patch: now redundant (same
 * var(--text)), kept because removing them is pure churn.
 *
 * Structure mirrors Mission Control
 * (klaxiora.css .k-metric + the reference's two inner flex rows): label on
 * top, value and note bottom-aligned, 128px min-height, 160ms hover.
 * Label is uppercased in CSS rather than in the strings so the source stays
 * readable — the reference ships literal uppercase, this renders identically.
 */
export function OpsMetric({ label, value, note }: { label: string; value: string | number; note?: string }) {
  return (
    <div className="k-metric">
      <div className="flex items-center justify-between gap-2">
        <span className="k-metric-label uppercase">{label}</span>
      </div>
      <div className="flex flex-col gap-[7px]">
        <div className="flex items-baseline gap-2">
          <span className="k-metric-value text-ink-primary">{value}</span>
        </div>
        {note && <div className="k-metric-note">{note}</div>}
      </div>
    </div>
  )
}

export function OperationsOverview() {
  const tasks = useAsync(() => fetchTasks(), [])
  const incidents = useAsync(fetchIncidents, [])
  const assets = useAsync(fetchAssets, [])
  const sops = useAsync(fetchSops, [])
  const checks = useAsync(fetchQualityChecks, [])

  if (tasks.loading || incidents.loading || assets.loading || sops.loading || checks.loading) {
    return <LoadingState />
  }

  const now = new Date()
  const tk = taskKpis(tasks.data ?? [], now)
  const ik = incidentKpis(incidents.data ?? [], now)
  const feed = activityFeed(tasks.data ?? [])
  const openPanel = openIncidentsPanel(incidents.data ?? [])
  // '—' wherever the backing list failed — a degraded card never shows a
  // fabricated zero.
  const v = (failed: string | null, value: number | string) => (failed ? '—' : value)

  // Metric rows are data-driven so the k-metric markup is written once. No
  // delta and no status dot: Operations has no honest period-over-period
  // figure for any of these, and the contract's dot encodes a delta's
  // direction — rendering either would be decoration, not data.
  const scheduleMetrics = [
    { label: 'Team with tasks',   value: v(tasks.error, tk.teamWithTasks), note: 'People with at least one task' },
    { label: 'Tasks today',       value: v(tasks.error, tk.tasksToday),    note: 'Scheduled for today' },
    { label: 'Completion rate',   value: v(tasks.error, tk.completionRate === null ? '—' : `${tk.completionRate}%`), note: 'Across tasks this month' },
    { label: 'Scheduled tasks',   value: v(tasks.error, tk.scheduled),     note: 'Still to be completed' },
  ]
  const healthMetrics = [
    { label: 'Open incidents',        value: v(incidents.error, ik.open),
      note: incidents.error ? 'Incidents unavailable' : openIncidentsSubline(ik.open, ik.severeOpen) },
    { label: 'Assets need attention', value: v(assets.error, assetKpis(assets.data ?? [], now).attention), note: 'Maintenance or renewal due' },
    { label: 'SOPs published',        value: v(sops.error, (sops.data ?? []).length), note: 'Documented processes' },
    { label: 'Audit pass rate',       value: v(checks.error, (() => { const r = qcKpis(checks.data ?? [], now).passRate; return r === null ? '—' : `${r}%` })()), note: 'Across all checks' },
  ]

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Overview"
        subtitle="The department at a glance — schedule, incidents, assets, and standards."
      />

      {tasks.error && <SectionErrorNote message={tasks.error} />}
      <div className="grid grid-cols-4 gap-3">
        {scheduleMetrics.map(m => <OpsMetric key={m.label} {...m} />)}
      </div>

      <div className="k-eyebrow uppercase mt-2">Operational health</div>
      {incidents.error && <SectionErrorNote message={incidents.error} />}
      {assets.error && <SectionErrorNote message={assets.error} />}
      {sops.error && <SectionErrorNote message={sops.error} />}
      {checks.error && <SectionErrorNote message={checks.error} />}
      <div className="grid grid-cols-4 gap-3">
        {healthMetrics.map(m => <OpsMetric key={m.label} {...m} />)}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="k-card">
          <div className="k-card-head">
            <div>
              <div className="k-card-title text-ink-primary">Recent activity</div>
              <div className="k-card-sub">The five most recent items on the schedule</div>
            </div>
          </div>
          <div className="p-5 flex flex-col gap-1">
            {tasks.error ? (
              <SectionErrorNote message={tasks.error} />
            ) : feed.length === 0 ? (
              <EmptyState message="No recent activity" hint="Scheduled field tasks will show up here" />
            ) : (
              feed.map(item => (
                <div key={item.id} className="flex items-start gap-3 py-3 border-b border-edge-row-faint last:border-0">
                  <span className="k-dot mt-1.5" />
                  <div>
                    <div className="text-13 text-ink-primary">
                      <span className="font-semibold">{item.person}</span> {item.verb} {item.taskLabel}
                    </div>
                    <div className="k-metric-note mt-0.5">{item.timeLine}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="k-card">
          <div className="k-card-head">
            <div>
              <div className="k-card-title text-ink-primary">Open incidents</div>
              <div className="k-card-sub">Newest first</div>
            </div>
          </div>
          <div className="p-5 flex flex-col gap-1">
            {incidents.error ? (
              <SectionErrorNote message={incidents.error} />
            ) : openPanel.length === 0 ? (
              <EmptyState message="Nothing open" hint="Open incidents will show up here" />
            ) : (
              openPanel.map(inc => {
                const severe = severityIsSevere(inc.severity)
                return (
                  <div key={inc.id} className="flex items-start gap-3 py-3 border-b border-edge-row-faint last:border-0">
                    <span className="k-metric-dot mt-1.5 flex-shrink-0"
                      style={{ background: severe ? danger.DEFAULT : success.DEFAULT }} />
                    <div>
                      <div className="text-13 text-ink-primary flex items-center gap-2">
                        <span className="font-semibold">{inc.title}</span>
                        <span className="k-badge uppercase"
                          style={severe
                            ? { background: danger.bg, color: danger.text }
                            : { background: surface.panel, color: ink.secondary }}>
                          {severityLabel(inc.severity)}
                        </span>
                      </div>
                      <div className="k-metric-note mt-0.5">
                        {formatSopDate(inc.createdAt)}{inc.relatedEmployeeName ? ` · ${inc.relatedEmployeeName}` : ''}
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
